// API functions for scoring reference data (non-admin read-only).
// Used by risk scoring dropdowns and display components.

import type { ApiClient } from './client'

// --- Types ---

export interface LikelihoodLevel {
  id: string
  level_number: number
  label: string
  description?: string | null
}

export interface ActiveLikelihood {
  version_id: string
  version_number: number
  activated_at: string
  levels: LikelihoodLevel[]
}

export interface ImpactTableWithLevels {
  id: string
  name: string
  description?: string | null
  levels: LikelihoodLevel[] // same shape as likelihood levels
}

export interface ImpactAssessment {
  id: string
  impact_table_id: string
  impact_table_name: string
  impact_level_id: string
  impact_level_name: string
  level_number: number
  score_type: 'residual' | 'inherent' | 'target'
}

// --- API ---

export interface AppetiteLookup {
  taxonomy_node_id: string
  taxonomy_node_name: string
  statement: string | null
  permitted_cells: { likelihood_level: number; impact_level: number }[]
}

export interface TaxonomyNode {
  id: string
  parent_id: string | null
  path: string
  depth: number
  name: string
  description?: string | null
}

export interface ActiveTaxonomy {
  version_id: string
  version_number: number
  activated_at: string | null
  activated_by: string | null
  nodes: TaxonomyNode[]
}

export function scoringApi(api: ApiClient) {
  return {
    /** Active likelihood levels for dropdowns. */
    getLikelihood: () => api.get<ActiveLikelihood>('/scoring/likelihood'),

    /** Active impact tables with levels inlined. */
    getImpactTables: () => api.get<ImpactTableWithLevels[]>('/scoring/impact-tables'),

    /** Current impact assessments for a risk (with resolved names). */
    getRiskImpactAssessments: (riskId: string) =>
      api.get<ImpactAssessment[]>(`/risks/${riskId}/impact-assessments`),

    /** Resolve appetite for a taxonomy node (walks up tree to find nearest definition). */
    resolveAppetite: (taxonomyNodeId: string) =>
      api.get<AppetiteLookup>(`/scoring/appetite?taxonomy_node_id=${taxonomyNodeId}`),

    /** Active taxonomy nodes for pickers/filters. */
    getTaxonomyNodes: () => api.get<ActiveTaxonomy>('/scoring/taxonomy-nodes'),
  }
}
